/**
 * Shared building blocks of the staircase sketch-object types (stair,
 * stair_l, stair_u, stair_spiral): the walk-line arrow every stair plan
 * symbol carries, the step ↔ step-count sync contract, and the split of a
 * step total over two flights.
 */

import { getSketchObjectDepthProperty, getSketchObjectWidthProperty } from '@pointorama/potree-shared';
import { at, pushPolyline, pushSegment } from '../glyphs/frame';
import { getNumberParamProperty } from '../paramSchema';
import type { SketchObjectFrame } from '../types';

/** Two coupled step values never fight below this difference (m). */
export const STEP_EPS = 1e-4;

/** Footprint length a flight always keeps beside its landing (m) — the
 *  flight-width clamp leaves at least this much run. */
export const MIN_FLIGHT_RUN = 0.2;

/**
 * Push a stair walk line through frame-coord points `[u, n][]`: the polyline
 * itself, a cross tick at the start (bottom of the stairs) and a chevron
 * arrow head at the end (top). `reversed` walks the same path the other way,
 * so the up direction can be corrected without rotating the object.
 */
export function pushWalkArrow(out: number[], frame: SketchObjectFrame, points: ReadonlyArray<readonly [number, number]>, reversed: boolean, headSize: number): void {
  if (points.length < 2) return;
  const pts = reversed ? [...points].reverse() : points;
  pushPolyline(out, frame, pts);

  const dirBetween = (p: readonly [number, number], q: readonly [number, number]): readonly [number, number] | null => {
    const du = q[0] - p[0];
    const dn = q[1] - p[1];
    const len = Math.hypot(du, dn);
    return len < 1e-9 ? null : [du / len, dn / len];
  };

  const start = pts[0];
  const startDir = dirBetween(start, pts[1]);
  if (startDir) {
    const [du, dn] = startDir;
    pushSegment(out, at(frame, start[0] - dn * headSize, start[1] + du * headSize), at(frame, start[0] + dn * headSize, start[1] - du * headSize), frame.z);
  }

  const head = pts[pts.length - 1];
  const headDir = dirBetween(pts[pts.length - 2], head);
  if (headDir) {
    const [du, dn] = headDir;
    pushSegment(out, at(frame, head[0], head[1]), at(frame, head[0] - (du + dn) * headSize, head[1] - (dn - du) * headSize), frame.z);
    pushSegment(out, at(frame, head[0], head[1]), at(frame, head[0] - (du - dn) * headSize, head[1] - (dn + du) * headSize), frame.z);
  }
}

/**
 * Split a two-flight staircase's steps: total count from the tread depth over
 * the combined run, allocated to the flights by their share of that run (each
 * flight keeps at least one step). Glyphs stretch each flight's treads evenly,
 * so the drawn total always equals the synced `number of steps`.
 */
export function splitFlightSteps(runA: number, runB: number, treadDepth: number): { countA: number; countB: number } {
  const total = Math.max(2, Math.round((runA + runB) / Math.max(0.05, treadDepth)));
  const countA = Math.min(total - 1, Math.max(1, Math.round((total * runA) / (runA + runB))));
  return { countA, countB: total - countA };
}

/** Footprint keys whose change moves a stair's walkable run. */
const RUN_KEYS: readonly string[] = ['width', 'depth', 'flightWidth'];

/**
 * Step ↔ step-count sync for a stair type whose walkable run derives from the
 * footprint — the multi-flight generalization of the straight stair's
 * contract (see stair.ts for why the mutual updates terminate):
 *  - number of steps changed → step := run / count;
 *  - step or footprint changed → count := round(run / step).
 * `totalRun` computes the walkable run from the live width/depth and the
 * type's `flightWidth` param (it must clamp the flight width exactly like the
 * glyph does, so panel values and drawn treads agree).
 */
export function makeStairStepSync(typeId: string, totalRun: (width: number, depth: number, flightWidth: number) => number): (objectId: string, changedKey: string) => void {
  return (objectId, changedKey) => {
    if (!RUN_KEYS.includes(changedKey) && changedKey !== 'treadDepth' && changedKey !== 'stepCount') return;
    let width: number;
    let depth: number;
    try {
      width = getSketchObjectWidthProperty(objectId).value ?? 0;
      depth = getSketchObjectDepthProperty(objectId).value ?? 0;
    } catch {
      return; // property service not ready
    }
    const flightWidth = getNumberParamProperty(typeId, 'flightWidth', objectId).value ?? 0;
    const run = totalRun(width, depth, flightWidth);
    if (run <= 0) return;
    const stepProperty = getNumberParamProperty(typeId, 'treadDepth', objectId);
    const countProperty = getNumberParamProperty(typeId, 'stepCount', objectId);
    if (changedKey === 'stepCount') {
      const count = Math.max(2, Math.round(countProperty.value ?? 2));
      const step = run / count;
      if (Math.abs((stepProperty.value ?? 0) - step) > STEP_EPS) stepProperty.value = step;
    } else {
      const step = Math.max(0.05, stepProperty.value ?? 0.25);
      const count = Math.max(2, Math.round(run / step));
      if (countProperty.value !== count) countProperty.value = count;
    }
  };
}
